import React, { useState, useEffect, useContext, useCallback } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Joi from "joi";
import { Button } from "react-bootstrap";
import Input from "../components/common/input";
import Select from "../components/common/select";
import Checkbox from "../components/common/checkbox";
import ListGroup from "../components/common/listGroup";
import BadgeList from "../components/common/badgeList";
import Pagination from "../components/common/pagination";
import Loading from "../components/common/loading";
import GrabberTable from "../components/grabberTable";
import GrabberChart from "../components/grabberChart";
import GrabberModal from "../components/grabberModal";
import ExportModal from "../components/grabberExportModal";
import http from "../services/http";
import { paginate } from "../utils/paginate";
import UserContext from "../context/userContext";
import SidebarContext from "../context/sidebarContext";
import SelectDateRange from "../components/common/selectDateRange";

const Grabber = () => {
  const [user] = useContext(UserContext);
  const [sidebar] = useContext(SidebarContext);

  const [operations, setOperations] = useState([]);
  const [personalGrabbers, setPersonalGrabbers] = useState([]);
  const [sharedCategories, setSharedCategories] = useState([]);
  const [sharedGrabbers, setSharedGrabbers] = useState([]);
  const [id, setId] = useState("");
  const [personalId, setPersonalId] = useState("");
  const [sharedId, setSharedId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [site, setSite] = useState({ id: "", name: "" });

  const [fromDate, setFromDate] = useState(moment().subtract(7, "days"));
  const [toDate, setToDate] = useState(moment());
  const [operation, setOperation] = useState("");
  const [operationCode, setOperationCode] = useState("");
  const [operationType, setOperationType] = useState("");

  const [code, setCode] = useState("");

  const [parameters, setParameters] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchResults, setSearchResults] = useState([]);

  const [axis, setAxis] = useState({ id: "Primary", name: "Primary" });
  const [chartCodes, setChartCodes] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [saveName, setSaveName] = useState("");

  const [showExportModal, setShowExportModal] = useState(false);
  const [showCodeList, setShowCodeList] = useState(true);
  const [showDataTable, setShowDataTable] = useState(true);

  const pageSize = 100;
  const navigation = '';
  const [page, setPage] = useState(1);

  const types = [
    { id: "BC", name: "Campaign" },
    { id: "JR", name: "Juice Run" },
  ];

  const axes = [
    { id: "Primary", name: "Primary" },
    { id: "Secondary", name: "Secondary" },
  ];

  const sites = [
    { id: "BA", name: "Bardney" },
    { id: "BU", name: "Bury" },
    { id: "CA", name: "Cantley" },
    { id: "NE", name: "Newark" },
    { id: "WI", name: "Wissington" },
  ];

  useEffect(() => {
    document.title = "FTR - Grabber";
    return () => {
      document.title = "FTR";
    };
  }, []);

  const getHiddenId = useCallback(async () => {
    try {
      const { data } = await http.get(`/stored-grabber/me`);
      setId(data.ID);
    }
    catch {
      //ignore error
    }
  }, []);

  // User Mount Effect
  useEffect(() => {
    async function getOperations() {
      try {
        const { data } = await http.get(`/operations`);
        setOperations(data.slice(0, 20));
      }
      catch {
        //ignore error
      }
    }

    async function getPersonalGrabbers() {
      try {
        const { data } = await http.get(`/stored-grabbers/1`);
        setPersonalGrabbers(data);
      }
      catch {
        //ignore error
      }
    }

    async function getSharedCategories() {
      try {
        const { data } = await http.get(`/report-categories`);
        setSharedCategories(data);
      }
      catch {
        //ignore error
      }
    }

    if (user) {
      getOperations();
      getPersonalGrabbers();
      getSharedCategories();
      getHiddenId();
      if (user.SITE !== "CO") setSite({ id: user.SITE });
    }
  }, [user, getHiddenId]);

  // Reports Effect
  useEffect(() => {
    async function getSharedGrabbers() {
      try {
        const { data } = await http.get(`/stored-grabbers/2/${categoryId}`);
        setSharedGrabbers(data);
      }
      catch {
        //ignore error
      }
    }

    if (categoryId) {
      getSharedGrabbers();
    }
  }, [user, categoryId]);

  // Header Effect
  useEffect(() => {
    async function getParameters() {
      setParameters([]);

      try {
        const { data } = await http.get(`/stored-grabber/header/${id}`);
        setParameters(data);
      }
      catch {
        //ignore error
      }
    }

    if (user && id) {
      getParameters();
    }
  }, [user, id]);

  const validate = useCallback((body) => {
    const schema = Joi.object({
      id: Joi.number().integer().required(),
      fDate: Joi.date().required(),
      tDate: Joi.date().required(),
      operation: Joi.string().allow("").optional(),
    });
    return schema.validate(body);
  }, []);

  // Data Effect
  useEffect(() => {
    async function getData() {
      const fDate = moment(fromDate).format("YYYY-MM-DD");
      const tDate = moment(toDate).format("YYYY-MM-DD");
      const body = { id, fDate, tDate, operation };
      const { error } = validate(body);
      if (error) return setData([]);
      setLoading(true);
      const op = operationCode ? "/" + operationCode : "";

      try {
        const { data } = await http.get(`/stored-grabber/${id}/${fDate}/${tDate}${op}`);
        setData(data);
      }
      catch {
        //ignore error
      }

      setLoading(false);

    }
    if (user && id) {
      getData();
    }
  }, [user, id, fromDate, toDate, operation, validate]);

  function handleGrabberSelect({ currentTarget: input }, type) {
    if (type === "P") {
      // Personal
      setSharedId("");
      setPersonalId(input.value);
      setId(input.value);
    } else if (type === "S") {
      // Shared
      setPersonalId("");
      setSharedId(input.value);
      setId(input.value);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();

    if (site.id === "") {
      return toast.error("invalid Site");
    }
    const submit = {
      REPT_ID: id,
      CODE: code.toUpperCase(),
      SITE: site.id,
    };

    try {
      const { data: newParameter } = await http.post(`/report-parameters`, submit);
      setParameters([...parameters, newParameter]);

      setLoading(true);
      const fDate = moment(fromDate).format("YYYY-MM-DD");
      const tDate = moment(toDate).format("YYYY-MM-DD");
      
      const { data } = await http.get(`/stored-grabber/${id}/${fDate}/${tDate}`);
      setLoading(false);
      setData(data);
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  async function handleSearch() {
    if (!code) return;
    try {
      const { data: searchResults } = await http.get(`/parameters?search=${code}`);
      setSearchResults(searchResults);
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  async function handleBadgeClick(parameter) {
    const oldParameters = [...parameters];
    setParameters(parameters.filter((p) => p.LABEL !== parameter.LABEL));
    try {
      await http.delete(`/report-parameters/${parameter.ID}`);

      const fDate = moment(fromDate).format("YYYY-MM-DD");
      const tDate = moment(toDate).format("YYYY-MM-DD");

      const { data } = await http.get(`/stored-grabber/${id}/${fDate}/${tDate}`);
      setData(data);
    } catch (ex) {
      setParameters(oldParameters);
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  function handleTableClick(parm) {
    const newChartCode = {
      code: `${parm.CODE}-${parm.SITE}`,
      axis: axis.id,
    };
    setChartCodes((c) => {
      return [...c, newChartCode];
    });
  }

  function handleHideChart() {
    setChartCodes([]);
  }

  function handleExportButton() {
    setShowExportModal(true);
  }


  async function handleExport(transpose, sort = "asc") {
    if (parameters.length === 0) return toast.info("No data to export");

    const personalIndex = personalGrabbers.findIndex(
      (p) => p.ID === parseInt(id)
    );
    const sharedIndex = sharedGrabbers.findIndex((p) => p.ID === parseInt(id));
    let filename = 'UnsavedGrabber';
    if (personalIndex >= 0)
      filename = personalGrabbers[personalIndex].REPORT_NAME;
    if (sharedIndex >= 0) filename = sharedGrabbers[sharedIndex].REPORT_NAME;

    try {
      const fDate = moment(fromDate).format("YYYY-MM-DD");
      const tDate = moment(toDate).format("YYYY-MM-DD");
      const op = operationCode ? "/" + operationCode : "";
      const { data: file } = await http.post(`/stored-grabber/export/${id}/${fDate}/${tDate}${op}`, {
          transpose,
          filename,
          sort,
        });

      const newFilename = filename + ' ' + fDate + ' to ' + tDate + '.csv';

      const options = {
        suggestedName: newFilename,
        types: [{
          description: "CSV file",
          accept: {'text/plain': ['.csv']}
        }]
      };

      const newHandle = await window.showSaveFilePicker(options);

      const writableStream = await newHandle.createWritable();
      await writableStream.write(file);
      await writableStream.close(); 

      setShowExportModal(false);      
      toast.info("Data exported to " + newHandle.name);
    }
    catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  function handleSaveAs() {
    if (parameters.length === 0) return toast.info("No parameters to save");
    setSaveName("");
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    try {
      const { data: newGrabber } = await http.post(`/grabber`, {
        name: saveName,
        sourceId: id,
      });
      setPersonalGrabbers([newGrabber, ...personalGrabbers]);
      setId(newGrabber.ID);
      setPersonalId(newGrabber.ID);
      toast.success("Grabber Created");
      setShowModal(false);
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
  }

  const handleRefresh = async () => {
    setLoading(true);
    const fDate = moment(fromDate).format("YYYY-MM-DD");
    const tDate = moment(toDate).format("YYYY-MM-DD");

    try {
      const { data } = await http.get(`/stored-grabber/${id}/${fDate}/${tDate}`);
      setData(data);
    }
    catch {
      //ignore error
    }

    setLoading(false);
  };

  const handleClearAll = async () => {
    if (sharedId !== "") return toast.info("Cannot clear Shared");
    const oldParameters = [...parameters];
    const oldData = [...data];
    setParameters([]);
    setData([]);
    try {
      await http.delete(`/report-parameters/all/${id}`);
      toast.success("Parameters Cleared");
    } catch (ex) {
      setParameters(oldParameters);
      setData(oldData);
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  };

  const pagedData = paginate(data, page, pageSize);

  function handleFromChange({ currentTarget: input }) {
    setFromDate(moment(input.value));
  }

  function handleToChange({ currentTarget: input }) {
    setToDate(moment(input.value));
  }

  function updateOperation(index) {
    if (index < 2) {
      setOperation("");
      setOperationCode("");
    }
    else {
      setOperation(operations[index - 2].COMMENTS);
      setOperationCode(operations[index - 2].OPTN_CODE);
    }
  }

  return (
    <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
      <div className="row">
        <div className="col">
          <Select
            name="personalGrabber"
            label="Personal Grabbers"
            placeholder="Grabber"
            value={personalId}
            options={personalGrabbers}
            onChange={(e) => handleGrabberSelect(e, "P")}
            listKey="ID"
            listName="REPORT_NAME"
          />
          {personalId || sharedId ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setPersonalId("");
                setSharedId("");
                getHiddenId();
              }}
            >
              Reset
            </button>
          ) : null}
        </div>
        <div className="col">
          <Select
            name="categories"
            label="Shared Categories"
            placeholder="Category"
            value={categoryId}
            options={sharedCategories}
            onChange={(e) => setCategoryId(e.currentTarget.value)}
            listKey="ID"
            listName="NAME"
          />
          <Select
            name="sharedGrabber"
            label="Shared Grabbers"
            placeholder="Grabber"
            value={sharedId}
            options={sharedGrabbers}
            onChange={(e) => handleGrabberSelect(e, "S")}
            listKey="ID"
            listName="REPORT_NAME"
          />
        </div>
      </div>
      <hr className="bg-success" />
      <div className="row">
        <div className="col">
          <ListGroup
            label="Site"
            items={sites}
            onItemSelect={(e) => setSite(e)}
            selectedItem={site}
          />
        </div>
        <div className="col">
        <SelectDateRange
          label="Date Range"
          value={fromDate.format("YYYY-MM-DD")}
          value2={toDate.format("YYYY-MM-DD")}
          onFromChange={handleFromChange}
          onToChange={handleToChange}
        />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Select
            name="operation"
            label="Operation"
            placeholder="Operation"
            value={operation}
            options={[{ CODE: "" }, ...operations]}
            onChange={(e) => { updateOperation(e.currentTarget.selectedIndex) }}
            listKey="CODE"
            listName="COMMENTS"
          />
        </div>
        <div className="col">
          <Select
            name="operationType"
            label="Operation Type (Not in use)"
            placeholder="Type"
            value={operationType}
            options={[{ id: "" }, ...types]}
            onChange={(e) => setOperationType(e.currentTarget.value)}
          />
        </div>
      </div>
      <div className="row pl-2">
        <div className="col-auto">
          <Checkbox
            name="showCodes"
            label="Show Parameters"
            checked={showCodeList}
            onClick={() => setShowCodeList(!showCodeList)}
          />
        </div>
        <div className="col-auto">
          <Checkbox
            name="showData"
            label="Show Data"
            checked={showDataTable}
            onClick={() => setShowDataTable(!showDataTable)}
          />
        </div>
      </div>
      <hr className="bg-success" />
      <form className="form-row">
        <div className="col-auto">
          <Input
            name="code"
            placeholder="Parameter Code"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={handleAdd}>
            Add
          </button>
        </div>
        <div className="col-auto">
          <Button variant="secondary" onClick={handleSearch}>
            Search
          </Button>
        </div>
        <div className="col-auto">
          {searchResults.length > 0 && (
            <Button variant="secondary" onClick={() => setSearchResults([])}>
              Clear Search
            </Button>
          )}
        </div>
      </form>
      {searchResults.length > 0 && (
        <div className="row">
          <div className="col-auto">
            <div
              className="list-group overflow-auto"
              style={{
                maxHeight: "200px",
                position: "absolute",
                width: "30rem",
                zIndex: 99,
                boxShadow:
                  "0 4px 6px -1px rgba(0,0,0,.1),0 2px 4px -1px rgba(0,0,0,.06)",
              }}
            >
              <ul className="list-group">
                {searchResults.map((parameter) => (
                  <li
                    className="list-group-item"
                    key={parameter.CODE}
                    onClick={() => setCode(parameter.CODE)}
                  >
                    {parameter.CODE} - {parameter.NAME}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="row justify-content-end align-items-center mx-3">
        <div className="col-auto">
          <button
            className="btn btn-outline-danger"
            onClick={handleClearAll}
          >
            Clear Codes
          </button>
        </div>
        <div className="col-auto">
          <button className="btn btn-outline-success" onClick={handleRefresh}>
            <i className="fas fa-sync"></i>
          </button>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-outline-warning"
            onClick={handleExportButton}
            disabled={data.length <= 0}
          >
            Export
          </button>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary mr-3"
            onClick={handleSaveAs}
            disabled={parameters.length <= 0}
          >
            Save As
          </button>
        </div>
      </div>
      {showCodeList && (
        <div className="row">
          <div className="col">
            <BadgeList
              list={parameters}
              listName="LABEL"
              onClick={handleBadgeClick}
            />
          </div>
        </div>
      )}
      <div className="row">
        <div className="col">
          {1 === 0 && (
            <ListGroup
              items={axes}
              onItemSelect={(a) => setAxis(a)}
              selectedItem={axis}
            />
          )}
          <GrabberChart
            data={data}
            codes={chartCodes}
            handleHideChart={handleHideChart}
          />
        </div>
      </div>
      {loading && <Loading />}
      {showDataTable ? (
        <div className="row">
          <GrabberTable
            loading={loading}
            dataCount={data.length}
            data={pagedData}
            parameters={parameters}
            onClick={handleTableClick}
          />
          <Pagination
            itemsCount={data.length}
            pageSize={pageSize}
            currentPage={page}
            onPageChange={(p) => setPage(p)}
            navigation={navigation}
          />
        </div>
      ) : (
        <p className="lead">{data.length} Rows Fetched</p>
      )}
      <GrabberModal
        show={showModal}
        data={saveName}
        handleClick={handleSave}
        handleChange={(e) => setSaveName(e.currentTarget.value)}
        handleClose={() => setShowModal(false)}
      />
      <ExportModal
        show={showExportModal}
        handleClose={() => setShowExportModal(false)}
        onSubmit={handleExport}
      />
    </div>
  );
};

export default Grabber;
